<script setup lang="ts">
  defineOptions({
    name: 'ChatWindowHeader',
  })

  import { computed, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { Expand01Icon, Minimize01Icon, XCloseIcon } from '@gohighlevel/ghl-icons/24/outline'

  import CPAvatar from '@ui/components/CPAvatar.vue'

  import CPButton from '@ui/components/CPButton.vue'

  import CPText from '@ui/components/CPText.vue'

  import ChatConversationOptionsMenu from './ChatConversationOptionsMenu.vue'

  import type { IConversationRecipient } from '../../types/conversation.types'

  const props = defineProps({
    recipient: {
      type: Object as PropType<IConversationRecipient | null>,
      default: null,
    },
    isMinimized: {
      type: Boolean,
      default: false,
    },
    showMoreOptions: {
      type: Boolean,
      default: true,
    },
    isBlocked: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits<{
    toggleMinimize: []
    viewProfile: []
    blockConversation: []
    unblockConversation: []
    deleteConversation: []
    close: []
  }>()

  const { t } = useI18n()

  const displayName = computed(() => props.recipient?.fullName || t('chats.user.unknownUser'))

  const moreOptionsDropdownId = computed(() => {
    const identity = props.recipient?.recipientId || props.recipient?.slug || props.recipient?.fullName || 'default'
    const safeIdentity = String(identity).replace(/[^a-zA-Z0-9_-]/g, '-')
    return `chat-window-more-options-${safeIdentity}-dropdown`
  })
</script>

<template>
  <div
    class="flex cursor-pointer items-center gap-3 border-b border-default bg-background px-4 py-3"
    role="button"
    tabindex="0"
    @click="emit('toggleMinimize')"
    @keydown.enter="emit('toggleMinimize')"
    @keydown.space.prevent="emit('toggleMinimize')"
  >
    <!-- User Avatar -->
    <CPAvatar :src="recipient?.avatar" size="md" :title="displayName" />

    <!-- User Info - vertically centered -->
    <div class="flex min-w-0 flex-1 items-center">
      <CPText size="md" weight="semibold" class="truncate text-body">
        {{ displayName }}
      </CPText>
    </div>

    <!-- Action Buttons -->
    <div class="flex items-center gap-1" @click.stop>
      <!-- Expand/Minimize -->
      <CPButton
        id="chat-window-toggle-minimize"
        type="text"
        size="sm"
        :title="isMinimized ? t('chats.actions.expand') : t('chats.actions.minimize')"
        class="h-6 w-6 p-0"
        @click="emit('toggleMinimize')"
      >
        <Expand01Icon v-if="isMinimized" class="h-4 w-4 text-secondary" />
        <Minimize01Icon v-else class="h-4 w-4 text-secondary" />
      </CPButton>

      <!-- More Options -->
      <ChatConversationOptionsMenu
        v-if="showMoreOptions"
        trigger-id="chat-window-more-options"
        :dropdown-id="moreOptionsDropdownId"
        button-class="h-6 w-6 p-0"
        icon-class="h-4 w-4 text-secondary"
        placement="bottom-end"
        :is-blocked="isBlocked"
        @view-profile="emit('viewProfile')"
        @block="emit('blockConversation')"
        @unblock="emit('unblockConversation')"
        @delete-chat="emit('deleteConversation')"
      />

      <!-- Close Button -->
      <CPButton id="chat-window-close" type="text" size="sm" :title="t('chats.actions.close')" class="h-6 w-6 p-0" @click="emit('close')">
        <XCloseIcon class="h-4 w-4 text-secondary" />
      </CPButton>
    </div>
  </div>
</template>
